import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import moment from 'moment';
import { UserDetailsContext } from '../context/user-details/user-details'
import ReportPeriodService from '../shared/services/report-period/report-period.service';
import { IDeadlineReminder } from '../shared/services/report-period/report-period.model';
import { usePdfExportMode } from '../hooks/use-pdf-export-mode';
import gaiaTheme from './theme';

const periodLabel = (reminder: IDeadlineReminder): string => {
  if (reminder.friendlyName) return reminder.friendlyName;
  if (reminder.startDate && reminder.endDate) {
    return `${String(reminder.startDate).slice(0, 10)} to ${String(reminder.endDate).slice(0, 10)}`;
  }
  return reminder.calendarYear ? String(reminder.calendarYear) : 'your reporting period';
};

const deadlineCopy = (reminder: IDeadlineReminder): string => {
  // moment parses the DATEONLY string as local midnight, so the rendered day matches the stored
  // date in every timezone and agrees with the period detail page's formatting.
  const dateLabel = moment(reminder.reportingDeadline).format('D MMMM YYYY');
  if (reminder.daysRemaining < 0) {
    return `The ${periodLabel(reminder)} report was due on ${dateLabel}.`;
  }
  if (reminder.daysRemaining === 0) {
    return `The ${periodLabel(reminder)} report is due today (${dateLabel}).`;
  }
  return `The ${periodLabel(reminder)} report is due on ${dateLabel}: ${reminder.daysRemaining} day${reminder.daysRemaining === 1 ? '' : 's'} remaining.`;
};

// Shown to org admins while a reporting period's user-set deadline is inside (or past) its
// reminder window. View period goes to the period settings where the deadline is managed;
// Dismiss is persisted server-side so it stays dismissed across sessions.
// `suppressed` yields to the factor-year banner (same fixed geometry, one banner at a time;
// the more actionable review banner wins, and this one reappears once it clears).
// leftOffset: width in px of the left rail chrome. The rail's Drawer paints above this
// fixed banner, so the banner starts at the rail's right edge and centres its content in
// the visible area. 0 on mobile / wherever the rail is hidden.
export const ReportDeadlineBanner = ({ suppressed = false, leftOffset = 0 }: { suppressed?: boolean, leftOffset?: number }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pdfExportMode = usePdfExportMode();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const userDetailsContext = useContext(UserDetailsContext);
  const [reminders, setReminders] = useState<IDeadlineReminder[]>([]);
  const reportPeriodService = new ReportPeriodService();

  const isAdmin = userDetailsContext.userDetails?.roles?.includes('admin') ?? false;

  const loadReminders = useCallback(() => {
    reportPeriodService.getDeadlineReminders()
      .then(response => setReminders(response || []))
      .catch(() => setReminders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadReminders();
    } else {
      setReminders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, userDetailsContext.userDetails?.organisation?.id]);

  if (suppressed) return null;
  if (pdfExportMode) return null;
  if (!isAdmin) return null;
  if (reminders.length === 0) return null;
  // Hide on the destination page itself: the period settings already show the deadline.
  if (location.pathname.startsWith('/settings/company-settings/reporting-periods/')) return null;

  const reminder = reminders[0];
  // Internal staff browsing as a customer must not silently suppress the org's
  // notification: dismissal belongs to the org's own admins.
  const canDismiss = !userDetailsContext.userDetails?.isLoggedInAsCustomer;

  const handleDismiss = () => {
    reportPeriodService.dismissDeadlineReminder(reminder.id)
      .then(() => setReminders(previous => previous.filter(existing => existing.id !== reminder.id)))
      .catch(() => undefined);
  };

  return (
    // Fixed below the AppBar, matching the sibling banners' geometry (48px bar, top offset
    // 64 / 106 on mobile, centered content). The in-flow spacer reserves the same height so
    // page content clears the banner instead of sitting underneath it.
    <>
      <Box sx={{
        height: isMobileMode ? '70px' : '48px',
        width: `calc(100% - ${leftOffset}px)`,
        backgroundColor: 'secondary.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'fixed',
        zIndex: 1000,
        top: isMobileMode ? 106 : 64,
        left: `${leftOffset}px`,
        px: 3,
        gap: 2,
      }}>
        <Typography variant='body1' noWrap={!isMobileMode} sx={{ color: 'text.primary', minWidth: 0 }}>
          {deadlineCopy(reminder)}
          {reminders.length > 1 ? ` (+${reminders.length - 1} more)` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Button size='small' variant='contained' disableElevation onClick={() => navigate(`/settings/company-settings/reporting-periods/${reminder.id}`)}>
            View period
          </Button>
          {canDismiss && (
            // The theme defaults buttons to contained; Dismiss is the quiet action.
            <Button size='small' variant='text' onClick={handleDismiss} sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
              Dismiss
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ height: isMobileMode ? '70px' : '48px', width: '100%' }} />
    </>
  );
};
